import { api } from "./api";
import { audioBufferToWav } from "./wav";
import { unzipSync } from "fflate";

// Encode an AudioBuffer to a 16-bit WAV Blob (compact upload).
function bufferToWavBlob(buffer, name) {
  const wav = audioBufferToWav(buffer, 16);
  const safe = (name || "track").replace(/[^a-z0-9]/gi, "_").slice(0, 40);
  return new Blob([wav], { type: "audio/wav" });
}

/**
 * Send all stems to the server-side Pro Engine (pedalboard + pyloudnorm + librosa).
 * Returns a mastered 24-bit WAV Blob + a report (LUFS in/out, true peak, bpm...).
 */
export async function masterWithProEngine(tracks, settings = {}) {
  const form = new FormData();
  const meta = { tracks: [], settings };
  tracks.forEach((t, i) => {
    const buf = t.buffer; // original stem (clean), not the baked playBuffer
    form.append("files", bufferToWavBlob(buf, t.name), `${i}_${(t.name || "track").replace(/[^a-z0-9]/gi, "_")}.wav`);
    meta.tracks.push({
      name: t.name, category: t.category,
      gain_db: Number(t.gain) || 0, pan: t.pan ?? null, mute: !!t.mute,
    });
  });
  form.append("meta", JSON.stringify(meta));
  const res = await api.post("/engine/master", form, { responseType: "blob" });
  let report = {};
  try { report = JSON.parse(res.headers["x-mix-report"] || "{}"); } catch (e) { /* noop */ }
  return { blob: res.data, report };
}

/**
 * Generate a real formant-preserving choir from a single lead vocal.
 * Returns a ZIP Blob of harmony stems + a report (key, third major/minor...).
 */
export async function generateChoir(leadBuffer, settings = {}) {
  const form = new FormData();
  form.append("file", bufferToWavBlob(leadBuffer, "lead"), "lead.wav");
  form.append("settings", JSON.stringify(settings));
  const res = await api.post("/engine/harmonize", form, { responseType: "blob" });
  let report = {};
  try { report = JSON.parse(res.headers["x-choir-report"] || "{}"); } catch (e) { /* noop */ }
  return { blob: res.data, report };
}

/**
 * Procedural arranger: adds guitars/bass/drums/pads locked to the vocal's key+tempo.
 * Returns the ZIP blob (stems + mix), an object URL to the full mix for inline A/B
 * playback, and a report (key, bpm, progression, style).
 */
export async function addInstruments(leadBuffer, settings = {}) {
  const form = new FormData();
  form.append("file", bufferToWavBlob(leadBuffer, "lead"), "lead.wav");
  form.append("settings", JSON.stringify(settings));
  const res = await api.post("/engine/arrange", form, { responseType: "blob" });
  let report = {};
  try { report = JSON.parse(res.headers["x-arrange-report"] || "{}"); } catch (e) { /* noop */ }
  // extract mix_complet.wav + instrument stems from the zip
  let mixUrl = null;
  let stems = {};
  try {
    const buf = new Uint8Array(await res.data.arrayBuffer());
    const files = unzipSync(buf);
    const mix = files["mix_complet.wav"];
    if (mix) mixUrl = URL.createObjectURL(new Blob([mix], { type: "audio/wav" }));
    ["guitare.wav", "basse.wav", "batterie.wav", "nappe.wav"].forEach((n) => {
      if (files[n]) stems[n] = files[n];
    });
  } catch (e) { /* noop */ }
  return { blob: res.data, mixUrl, stems, report };
}

/**
 * Route A — neural "like Suno" via fal.ai (ACE-Step audio-to-audio).
 * Generates a real band following the vocal. Returns a playable/downloadable WAV.
 */
export async function generateBandAI(leadBuffer, style = "pop", prompt = "") {
  const form = new FormData();
  form.append("file", bufferToWavBlob(leadBuffer, "lead"), "lead.wav");
  form.append("style", style);
  form.append("prompt", prompt);
  const res = await api.post("/engine/generate-band", form, { responseType: "blob" });
  let report = {};
  try { report = JSON.parse(res.headers["x-band-report"] || "{}"); } catch (e) { /* noop */ }
  const url = URL.createObjectURL(res.data);
  return { blob: res.data, url, report };
}
